import { useState } from "react";
import { toast } from "sonner";
import { Loader2, Phone } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import api, { apiError } from "@/lib/api";

const empty = {
  customer_name: "", customer_phone: "", customer_email: "",
  address: "", suburb: "", num_cameras: 4, installation_type: "wired",
  price: "", notes: "",
};

export default function NewJobDialog({ open, onClose, onCreated }) {
  const [form, setForm] = useState(empty);
  const [busy, setBusy] = useState(false);
  const set = (k, v) => setForm({ ...form, [k]: v });
  const reset = () => setForm(empty);

  const submit = async () => {
    if (!form.customer_name.trim()) { toast.error("Customer name is required"); return; }
    setBusy(true);
    try {
      const { data } = await api.post("/admin/jobs", { ...form, price: parseFloat(form.price || 0) });
      toast.success("Job created — ready to schedule or invoice");
      reset();
      onCreated?.(data);
      onClose();
    } catch (e) { toast.error(apiError(e)); }
    finally { setBusy(false); }
  };

  return (
    <Dialog open={open} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-lg max-h-[85vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="font-display text-2xl flex items-center gap-2"><Phone className="h-5 w-5 text-eagle-gold" />New Job</DialogTitle>
        </DialogHeader>
        <p className="text-sm text-muted-foreground -mt-2 mb-2">Skip the quote step entirely — for a customer who's already agreed to a price on the phone.</p>
        <div className="space-y-3">
          <div className="grid grid-cols-2 gap-3">
            <div><Label>Customer name *</Label><Input value={form.customer_name} onChange={(e) => set("customer_name", e.target.value)} data-testid="new-job-name" /></div>
            <div><Label>Phone</Label><Input value={form.customer_phone} onChange={(e) => set("customer_phone", e.target.value)} data-testid="new-job-phone" /></div>
          </div>
          <div><Label>Email <span className="text-xs text-muted-foreground">(needed to email an invoice)</span></Label><Input type="email" value={form.customer_email} onChange={(e) => set("customer_email", e.target.value)} data-testid="new-job-email" /></div>
          <div className="grid grid-cols-2 gap-3">
            <div><Label>Address</Label><Input value={form.address} onChange={(e) => set("address", e.target.value)} data-testid="new-job-address" /></div>
            <div><Label>Suburb</Label><Input value={form.suburb} onChange={(e) => set("suburb", e.target.value)} data-testid="new-job-suburb" /></div>
          </div>
          <div className="grid grid-cols-3 gap-3">
            <div><Label># Cameras</Label><Input type="number" min="0" value={form.num_cameras} onChange={(e) => set("num_cameras", parseInt(e.target.value || 0))} data-testid="new-job-cameras" /></div>
            <div>
              <Label>Setup</Label>
              <Select value={form.installation_type} onValueChange={(v) => set("installation_type", v)}>
                <SelectTrigger data-testid="new-job-install-type"><SelectValue /></SelectTrigger>
                <SelectContent>{["wired", "wireless", "hybrid"].map(t => <SelectItem key={t} value={t}>{t}</SelectItem>)}</SelectContent>
              </Select>
            </div>
            <div><Label>Price (AUD)</Label><Input type="number" min="0" step="0.01" value={form.price} onChange={(e) => set("price", e.target.value)} data-testid="new-job-price" /></div>
          </div>
          <div><Label>Notes</Label><Textarea rows={2} value={form.notes} onChange={(e) => set("notes", e.target.value)} data-testid="new-job-notes" /></div>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={() => { reset(); onClose(); }}>Cancel</Button>
          <Button onClick={submit} disabled={busy} className="bg-eagle-gold text-eagle-ink hover:bg-eagle-goldHover" data-testid="new-job-submit">
            {busy && <Loader2 className="h-4 w-4 animate-spin mr-2" />}Create Job
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
